import React, { FormEvent, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import TaskAction from "../../../../interfaces/task/taskActions/TaskAction";
import { TaskActionState } from "../../../../interfaces/task/Task";
import { TaskActionUpdateRequest } from "../../../../interfaces/task/taskActions/TaskActionUpdateRequest";
import { updateTaskAction } from "../../../../services/taskServices/TaskActionService";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import "./UpdateTaskActivityModal.css";

interface UpdateTaskActivityProps {
  showUpdateActivityModal: boolean;
  handleCloseUpdateActivityModal: () => void;
  taskAction: TaskAction;
  authToken: string;
  refreshActivity: () => void;
}

const UpdateTaskActivityModal = ({
  handleCloseUpdateActivityModal,
  showUpdateActivityModal,
  taskAction,
  authToken,
  refreshActivity,
}: UpdateTaskActivityProps) => {
  const [taskActionForm, setTaskActionForm] = useState<TaskActionState>({
    Description: "",
    CompletionTime: new Date(),
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setTaskActionForm({
      Description: taskAction.description || "",
      CompletionTime: taskAction.completionTime
        ? new Date(taskAction.completionTime)
        : new Date(),
    });
    setFormErrors({});
  }, [taskAction]);

  const validateFields = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (!taskActionForm.Description.trim()) {
      errors.description = "Description is required.";
    }
    if (!taskActionForm.CompletionTime) {
      errors.completionTime = "Completion date is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitUpdateForm = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateFields()) {
      return;
    }

    const payload: TaskActionUpdateRequest[] = [
      {
        taskActionId: taskAction.id.toString(),
        description: taskActionForm.Description,
        completionTime: taskActionForm.CompletionTime,
        deleted: false,
      },
    ];

    setIsLoading(true);
    updateTaskAction(authToken, payload)
      .then((res) => {
        if (res.success) {
          toast.success(res.message);
          handleCloseUpdateActivityModal();
          refreshActivity();
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (!showUpdateActivityModal) return null;

  return (
    <>
      <div className="modal-activity-update-overlay">
        <div className="modal-activity-update-container">
          <div className="modal-activity-update-header">
            <h2 className="modal-activity-update-title">Update Activity</h2>
            <button
              className="modal-activity-update-close"
              onClick={handleCloseUpdateActivityModal}
              type="button"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <form
            onSubmit={handleSubmitUpdateForm}
            className="modal-activity-update-form"
          >
            <div className="modal-activity-update-body">
              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Description<span className="required">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  placeholder="A brief description of what the task entails..."
                  value={taskActionForm.Description}
                  onChange={(e) =>
                    setTaskActionForm({
                      ...taskActionForm,
                      Description: e.target.value,
                    })
                  }
                  className="form-textarea"
                  rows={5}
                />
                {formErrors.description && (
                  <span className="error-message">
                    {formErrors.description}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="completionDate" className="form-label">
                  Completion Date<span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="completionDate"
                  name="completionDate"
                  value={
                    taskActionForm.CompletionTime.toISOString().split("T")[0]
                  }
                  onChange={(e) =>
                    setTaskActionForm({
                      ...taskActionForm,
                      CompletionTime: new Date(e.target.value),
                    })
                  }
                  onKeyDown={(e) => e.preventDefault()}
                  className="form-input"
                  max={new Date().toISOString().split("T")[0]}
                />
                {formErrors.completionTime && (
                  <span className="error-message">
                    {formErrors.completionTime}
                  </span>
                )}
              </div>
            </div>

            <div className="modal-activity-update-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseUpdateActivityModal}
              >
                Cancel
              </button>
              {isLoading ? (
                <button type="button" className="btn btn-primary" disabled>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="btn-spinner"
                  />
                  Updating...
                </button>
              ) : (
                <button type="submit" className="btn btn-primary">
                  Update Activity
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UpdateTaskActivityModal;
